import {
  CheckOutlined,
  InfoCircleOutlined,
  WarningOutlined
} from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import classNames from "classnames";
import type { CustomInnerHeaderProps } from "ag-grid-react";
import {
  ACCOUNT_NAME_HEADER_TOOLTIP,
  getAddedByAuthorizedAt,
  getAddedByLabel
} from "pages/subscription-settings/integrations-tab-new/utils";
import { DateUtils, DATE_FORMAT_MMDDYYYY } from "utils/dateutils";

function ReauthorizeOverlay(props: any) {
  const { onReauthClick, reauthInProgress = false } = props;

  return (
    <div>
      <div className="absolute inset-0 z-10 bg-white opacity-70" />
      <div className="absolute top-[30%] -translate-y-2/4 left-[50%] -translate-x-2/4 z-20 bg-white border border-solid border-gray-500 px-6 py-3 flex items-baseline space-x-1">
        <div className="font-medium flex">
          <WarningOutlined className="text-orange-500 font-semibold inline-block" />
        </div>
        <div className="grow">
          Please Re-Authenticate your account.{" "}
          <Button
            size="small"
            type="text"
            className="font-medium workflow-document-upload"
            onClick={() => {
              onReauthClick();
            }}
            loading={reauthInProgress}
            data-testid="reauthenticate-button"
          >
            Click here
          </Button>{" "}
          to Authenticate
        </div>
      </div>
    </div>
  );
}

function SelectTagRenderer(props: {
  label: string;
  onDelete?: () => void;
  canDelete: boolean;
}) {
  const { label, onDelete, canDelete } = props;
  return (
    <span
      className="ant-select-selection-item"
      title="Trial account xagojyry@socam.me"
    >
      <span className="ant-select-selection-item-content">{label}</span>
      {canDelete && (
        <span
          className="ant-select-selection-item-remove"
          aria-hidden="true"
          onClick={() => {
            if (onDelete) onDelete();
          }}
        >
          <span role="img" aria-label="close" className="anticon anticon-close">
            <svg
              viewBox="64 64 896 896"
              focusable="false"
              data-icon="close"
              width="1em"
              height="1em"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 00203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z" />
            </svg>
          </span>
        </span>
      )}
    </span>
  );
}

function IntgWizardHeader(props: any) {
  const { integrationCompleted } = props;
  return (
    <div className="integration-wizard w-full flex items-center">
      <div className="pill completed">
        <div className="w-full text-center">Authorized</div>
        <div className="check-mark">
          <CheckOutlined />
        </div>
      </div>
      <div className="grow">
        <hr className="pill-connection completed" />
      </div>
      <div
        className={classNames("pill", {
          inprogress: !integrationCompleted,
          completed: integrationCompleted
        })}
      >
        <div className="w-full text-center">Select Account</div>
        <div className="check-mark">
          <CheckOutlined />
        </div>
      </div>
      <div className="grow">
        <hr
          className={classNames("pill-connection", {
            inprogress: !integrationCompleted,
            completed: integrationCompleted
          })}
        />
      </div>
      <div
        className={classNames("pill", {
          completed: integrationCompleted
        })}
      >
        <div className="w-full text-center">Integrated</div>
        <div className="check-mark">
          <CheckOutlined />
        </div>
      </div>
    </div>
  );
}

function ACCauthorizationNotDone() {
  return (
    <ul className="pl-3">
      <li>To integrate with Autodesk Forma, click Configure.</li>
      <li>Login with your Autodesk Forma credential.</li>
      <li>Select the accounts you want to configure.</li>
      <li>
        You need to be part of at least one project in each of the selected
        Autodesk Forma account(s).
      </li>
      <li>
        To connect with Autodesk Forma projects of above configured accounts, go
        to Project Settings.
      </li>
    </ul>
  );
}
function ACCauthorizationActive() {
  return (
    <>
      <p className="m-0 p-0">
        You are connected with Autodesk Forma. <br />
        Click <span className="font-semibold">View Details</span> to:
      </p>
      <ul className="list-none pl-3">
        <li>1. View or edit details of authorization.</li>
        <li>2. Configure more accounts.</li>
      </ul>
    </>
  );
}
function ACCauthorizationExpired() {
  return (
    <p className="m-0 p-0">
      Authorization has expired and integration with Autodesk Forma is no longer
      valid. Click reauthorize to renew the connection.
    </p>
  );
}

function AddedByCellRenderer(params: any) {
  const properties = params?.data?.properties;
  const label = getAddedByLabel(properties);
  const lastAuthorized = getAddedByAuthorizedAt(properties);
  const date = lastAuthorized
    ? DateUtils.format(lastAuthorized, DATE_FORMAT_MMDDYYYY)
    : "";

  if (!label && !date) return "";

  // Do not use colSecondValue here — its margin-top: -10px overlaps the name
  // on integrations rows. Mirror Updated At colors with normal stacking.
  return (
    <div className="ciq-added-by-cell">
      <div className="ciq-added-by-cell__name">{label || "—"}</div>
      {date ? <div className="ciq-added-by-cell__date">{date}</div> : null}
    </div>
  );
}

function addedByValueGetter(params: any) {
  // Filter/sort on name+email only. Including LAST_AUTHORIZED made one option
  // per authorization date; truncation hid the date so they looked like duplicates.
  return getAddedByLabel(params?.data?.properties) || undefined;
}

export const addedByColumnDef = {
  headerName: "Added By",
  headerTooltip: "Added By",
  minWidth: 180,
  autoHeight: false,
  wrapText: false,
  filter: "agSetColumnFilter",
  valueGetter: addedByValueGetter,
  cellRenderer: AddedByCellRenderer,
  cellClass: "ciq-cell-v-center"
};

/**
 * Disabled Select option label: gray name + info icon + status text
 * (project source dropdown and account/workspace pickers).
 */
export function AlreadyIntegratedOptionLabel(props: {
  label: string;
  message?: string;
}) {
  const { label, message = "Already integrated." } = props;
  // Reference: light name, medium icon, darker status text.
  return (
    <div className="flex items-center gap-1.5 min-w-0 max-w-full">
      <span className="truncate !text-black/25">{label}</span>
      <InfoCircleOutlined className="shrink-0 text-xs !text-black/45" />
      <span className="shrink-0 text-xs whitespace-nowrap !text-black/65">
        {message}
      </span>
    </div>
  );
}

/**
 * Inner header only — keeps ag-grid filter/sort chrome. Uses antd Tooltip so the
 * Account Name explanation always shows, even when the grid is set to
 * tooltipShowMode="whenTruncated".
 */
export function AccountNameInnerHeader(props: CustomInnerHeaderProps) {
  const { displayName } = props;
  return (
    <span className="ag-header-cell-text inline-flex items-center gap-1">
      {displayName}
      <Tooltip title={ACCOUNT_NAME_HEADER_TOOLTIP}>
        <InfoCircleOutlined className="text-xs text-neutral-500 cursor-help" />
      </Tooltip>
    </span>
  );
}

export const accountNameHeaderComponentParams = {
  innerHeaderComponent: AccountNameInnerHeader
};

export {
  ReauthorizeOverlay,
  SelectTagRenderer,
  IntgWizardHeader,
  ACCauthorizationNotDone,
  ACCauthorizationActive,
  ACCauthorizationExpired,
  AddedByCellRenderer,
  addedByValueGetter
};
